import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, DatePicker, Tag, Space,
  Typography, Alert, Modal, Upload, Checkbox, Radio, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TASK_TYPES = {
  unbind: { text: '设备解绑', color: 'blue' },
  limitVendor: { text: '限定卡商', color: 'orange' },
  disable: { text: '设备禁用', color: 'red' },
  reset: { text: '设备置新', color: 'green' },
  flowReset: { text: '流量置空', color: 'purple' },
};

const VENDORS = [
  { value: 'v1', label: '测试卡商' },
  { value: 'v2', label: '汇云宁波电信' },
  { value: 'v3', label: '妙月科技' },
];

const ENTERPRISES = [
  { value: 'ent1', label: '东莞康林物联网有限公司', id: 'ENT20231000001' },
  { value: 'ent2', label: '深圳智联科技有限公司', id: 'ENT20230750002' },
  { value: 'ent3', label: '上海瀚晖通讯技术有限公司', id: 'ENT20230820003' },
  { value: 'ent4', label: '北京云视安防有限公司', id: 'ENT20230910004' },
];

const TASK_STATUS = {
  pending: { text: '待发布', color: 'default' },
  publishing: { text: '发布中', color: 'processing' },
  paused: { text: '已暂停', color: 'warning' },
  terminated: { text: '已终止', color: 'error' },
};

const mockData = [
  { key: '1', taskType: 'unbind', total: 50, failed: 2, enterprise: '东莞康林物联网有限公司', enterpriseId: 'ENT20231000001', operator: 'admin@kanglin.com', operatorName: '系统管理员', createdAt: '2026-04-10 14:30:00', status: 'publishing' },
  { key: '2', taskType: 'limitVendor', subAction: 'limit', total: 120, failed: 0, enterprise: '深圳智联科技有限公司', enterpriseId: 'ENT20230750002', operator: 'ops@zhilian.com', operatorName: '运维工程师', createdAt: '2026-04-09 10:15:00', status: 'terminated' },
  { key: '3', taskType: 'disable', subAction: 'disable', total: 30, failed: 1, enterprise: '上海瀚晖通讯技术有限公司', enterpriseId: 'ENT20230820003', operator: 'admin@hanhui.com', operatorName: '管理员', createdAt: '2026-04-08 16:45:00', status: 'paused' },
  { key: '4', taskType: 'reset', total: 200, failed: 5, enterprise: '北京云视安防有限公司', enterpriseId: 'ENT20230910004', operator: 'root@yunshi.com', operatorName: '超级管理员', createdAt: '2026-04-07 09:00:00', status: 'pending' },
  { key: '5', taskType: 'limitVendor', subAction: 'cancel', total: 10, failed: 0, enterprise: '东莞康林物联网有限公司', enterpriseId: 'ENT20231000001', operator: 'ops@kanglin.com', operatorName: '运维工程师', createdAt: '2026-04-06 11:20:00', status: 'publishing' },
  { key: '6', taskType: 'disable', subAction: 'enable', total: 15, failed: 0, enterprise: '深圳智联科技有限公司', enterpriseId: 'ENT20230750002', operator: 'dev@zhilian.com', operatorName: '开发工程师', createdAt: '2026-04-05 08:30:00', status: 'terminated' },
  { key: '7', taskType: 'flowReset', total: 80, failed: 0, enterprise: '东莞康林物联网有限公司', enterpriseId: 'ENT20231000001', operator: 'ops@kanglin.com', operatorName: '运维工程师', createdAt: '2026-08-06 09:15:00', status: 'publishing' },
];

const DeviceMaintenance = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [createVisible, setCreateVisible] = useState(false);
  const [taskType, setTaskType] = useState(null);
  const [subAction, setSubAction] = useState(null);
  const [licenseText, setLicenseText] = useState('');
  const [cardText, setCardText] = useState('');
  const [resetMethod, setResetMethod] = useState('license');
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);

  const columns = [
    {
      title: '任务类型', dataIndex: 'taskType', width: 160,
      render: (v, r) => {
        const t = TASK_TYPES[v];
        const subText = { limit: '限定卡商', cancel: '取消限定', disable: '设备禁用', enable: '解除禁用' };
        return <span><Tag color={t.color}>{t.text}</Tag>{r.subAction && <Text type="secondary" style={{ fontSize: 12 }}>{subText[r.subAction]}</Text>}</span>;
      },
    },
    {
      title: '任务进度', dataIndex: 'status', width: 100,
      render: (v) => { const s = TASK_STATUS[v]; return <Tag color={s.color}>{s.text}</Tag>; },
    },
    { title: '设备总数', dataIndex: 'total', width: 100 },
    {
      title: '失败设备', dataIndex: 'failed', width: 100,
      render: (v) => v > 0 ? <Text type="danger">{v}</Text> : 0,
    },
    {
      title: '所属企业', dataIndex: 'enterprise', width: 220,
      render: (v, r) => (
        <div>
          <div>{v}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.enterpriseId}</Text>
        </div>
      ),
    },
    {
      title: '操作账号', dataIndex: 'operator', width: 180,
      render: (v, r) => (
        <div>
          <div>{r.operatorName}</div>
          <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>{v}</Text>
        </div>
      ),
    },
    { title: '发起时间', dataIndex: 'createdAt', width: 180 },
    {
      title: '操作', key: 'action', width: 260,
      render: (_, r) => (
        <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 4px' }}>|</span>}>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/device/maintenance/detail?id=${r.key}`)}>任务详情</Button>
          {r.status === 'pending' && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.success('任务已发布')}>发布任务</Button>}
          {r.status === 'pending' && <Button type="link" size="small" danger style={{ padding: 0 }} onClick={() => message.success('已删除')}>删除</Button>}
          {r.status === 'publishing' && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.success('任务已暂停')}>暂停发布</Button>}
          {r.status === 'publishing' && <Button type="link" size="small" danger style={{ padding: 0 }} onClick={() => message.success('任务已终止')}>终止发布</Button>}
          {r.status === 'paused' && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.success('任务已继续')}>继续发布</Button>}
          {r.status === 'paused' && <Button type="link" size="small" danger style={{ padding: 0 }} onClick={() => message.success('任务已终止')}>终止发布</Button>}
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    if (!selectedEnterprise) { message.warning('请选择企业'); return; }
    if (!taskType) { message.warning('请选择任务类型'); return; }
    if (taskType === 'unbind') {
      const lines = licenseText.split('\n').filter(l => l.trim());
      if (lines.length === 0) { message.warning('请至少输入一个License'); return; }
      if (lines.length > 20) { message.warning('最多输入20台设备'); return; }
    }
    if (taskType === 'flowReset') {
      const text = resetMethod === 'license' ? licenseText : cardText;
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      const label = resetMethod === 'license' ? 'License' : '4G卡号';
      if (lines.length === 0) { message.warning(`请至少输入一个${label}`); return; }
      if (lines.length > 20) { message.warning(`最多输入20个${label}`); return; }
    }
    message.success('任务已提交执行');
    setCreateVisible(false);
    setTaskType(null);
    setSubAction(null);
    setLicenseText('');
    setCardText('');
    setResetMethod('license');
    setSelectedEnterprise(null);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>设备运维</Title></div>

      <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
        message="你可对你的设备进行日常运维管理：设备解绑、限定卡商、设备禁用、设备置新、流量置空" />

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12 }} bodyStyle={{ padding: '12px 0' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item name="enterprise" style={{ marginRight: 8 }}>
            <Select placeholder="选择企业" allowClear style={{ width: 200 }} options={ENTERPRISES} />
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="searchType" initialValue="license" noStyle>
                <Select style={{ width: 110 }}>
                  <Option value="license">License</Option>
                  <Option value="did">设备DID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="searchKeyword" noStyle>
                <Input placeholder="请输入" allowClear style={{ width: 200 }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="dateRange" style={{ marginRight: 8 }}>
            <RangePicker placeholder={['开始时间', '结束时间']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setTaskType(null); setSelectedEnterprise(null); setCreateVisible(true); }}>创建任务</Button>
        </div>
        <Table columns={columns} dataSource={mockData} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 创建任务弹窗 */}
      <Modal title="创建任务" open={createVisible} onCancel={() => { setCreateVisible(false); setLicenseText(''); setCardText(''); setResetMethod('license'); setSelectedEnterprise(null); }} width={600} destroyOnClose
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => { setCreateVisible(false); setLicenseText(''); setCardText(''); setResetMethod('license'); setSelectedEnterprise(null); }}>取消</Button><Button type="primary" onClick={handleCreate}>立即执行</Button></div>}
      >
        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} message={
          <div style={{ fontSize: 12 }}>
            <div><Text strong>设备解绑：</Text>解除绑定用户，需要用户重新添加设备</div>
            <div><Text strong>限定卡商：</Text>限定卡商：对设备指定卡商4G卡上线；取消限定：已选设备变成不限任何卡商</div>
            <div><Text strong>设备禁用：</Text>设备禁用：设备无法上线；解除禁用：设备恢复可上线</div>
            <div><Text strong>设备置新：</Text>重置到未激活(产线状态)，置新完成后等同于初次激活</div>
            <div><Text strong>流量置空：</Text>将设备流量卡剩余流量清零；仅支持鹤梦云生态平台流量卡（汇云宁波电信、汇云华弘电信、广春华弘电信），其他卡商暂不支持</div>
          </div>
        } />

        <Form layout="vertical">
          <Form.Item label="选择企业" required>
            <Select placeholder="请选择目标企业" value={selectedEnterprise} onChange={setSelectedEnterprise} style={{ width: '100%' }} options={ENTERPRISES} />
            {selectedEnterprise && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>企业ID：<Text style={{ fontFamily: 'monospace' }}>{ENTERPRISES.find(e => e.value === selectedEnterprise)?.id}</Text></Text>
              </div>
            )}
          </Form.Item>

          <Form.Item label="任务类型" required>
            <Select placeholder="请选择任务类型" value={taskType} onChange={(v) => { setTaskType(v); setSubAction(null); }} style={{ width: '100%' }}>
              {Object.entries(TASK_TYPES).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>

          {taskType && (
            <>
              {/* 设备解绑：License文本域 */}
              {taskType === 'unbind' && (
                <Form.Item label="输入设备License" required>
                  <Input.TextArea
                    rows={6}
                    placeholder="每行一个License"
                    value={licenseText}
                    onChange={e => setLicenseText(e.target.value)}
                  />
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      每行一个License，最多输入20台设备
                    </Text>
                  </div>
                </Form.Item>
              )}

              {/* 流量置空：重置方式 + 文本域 */}
              {taskType === 'flowReset' && (
                <>
                  <Form.Item label="重置方式" required>
                    <Radio.Group value={resetMethod} onChange={e => setResetMethod(e.target.value)}>
                      <Radio value="license">按License</Radio>
                      <Radio value="card">按4G卡号</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item label={resetMethod === 'license' ? '输入设备License' : '输入4G卡号'} required>
                    <Input.TextArea
                      rows={6}
                      placeholder={resetMethod === 'license' ? '每行一个License' : '每行一个4G卡号'}
                      value={resetMethod === 'license' ? licenseText : cardText}
                      onChange={e => resetMethod === 'license' ? setLicenseText(e.target.value) : setCardText(e.target.value)}
                    />
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {resetMethod === 'license' ? '每行一个License，最多输入20台设备' : '每行一个4G卡号，最多输入20个'}
                      </Text>
                    </div>
                  </Form.Item>
                </>
              )}

              {/* 其他任务类型：文件上传 */}
              {taskType !== 'unbind' && taskType !== 'flowReset' && (
                <Form.Item label="上传设备文件" required>
                  <Space>
                    <Upload maxCount={1} beforeUpload={() => false} accept=".xlsx,.xls,.csv">
                      <Button icon={<UploadOutlined />}>上传文件</Button>
                    </Upload>
                    <Button type="link" icon={<DownloadOutlined />}>下载模板</Button>
                  </Space>
                  <div><Text type="secondary" style={{ fontSize: 12 }}>支持 .xlsx、.xls、.csv 格式</Text></div>
                </Form.Item>
              )}

              {taskType === 'limitVendor' && (
                <>
                  <Form.Item label="操作类型" required>
                    <Radio.Group onChange={(e) => setSubAction(e.target.value)} value={subAction}>
                      <Radio value="limit">限定卡商</Radio>
                      <Radio value="cancel">取消限定</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {subAction === 'limit' && (
                    <Form.Item label="指定卡商" required>
                      <Checkbox.Group options={VENDORS} />
                    </Form.Item>
                  )}
                </>
              )}

              {taskType === 'disable' && (
                <Form.Item label="操作类型" required>
                  <Radio.Group onChange={(e) => setSubAction(e.target.value)} value={subAction}>
                    <Radio value="disable">设备禁用</Radio>
                    <Radio value="enable">解除禁用</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceMaintenance;
